import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ModalManager from "@ebay/nice-modal-react";
import Table from "/src/components/table";
import InputSearch from "/src/components/UI/InputSearch/InputSearch";
import AutoComplete from "/src/components/UI/AutoComplete/AutoComplete";
import telephonyLogsService from "/src/features/administration/telephonyLogs/services/telephonyLogsService";
import companyService from "/src/features/administration/company/services/companyService";
import { formatDateWithoutSeconds } from "/src/utils/dateWithoutSeconds";
import getCutToken from "/src/utils/getCutToken";
import { switchCompany } from "/src/utils/switchCompany";
import { useToastDispatchContext } from "/src/layouts/toast/context/toastContext";
import TelephonyLogDetailsModal from "../TelephonyLogDetailsModal/TelephonyLogDetailsModal";
import "../../TelephonyLogs.css";

const LEVELS = [
    { value: "", label: "All", className: "all" },
    { value: "info", label: "Info", className: "info" },
    { value: "warn", label: "Warn", className: "warn" },
    { value: "error", label: "Error", className: "error" },
];

function CompanyCell({ logo, name, id, onClick }) {
    const [imgError, setImgError] = useState(false);

    if (!id) {
        return "-";
    }

    if (logo && !imgError) {
        return <img srcSet={logo} title={name} style={{ cursor: "pointer", height: 30 }} onClick={onClick} onError={() => setImgError(true)} />;
    }

    return (
        <span style={{ cursor: "pointer", color: "var(--main)" }} onClick={onClick}>
            {name || "-"}
        </span>
    );
}

function TelephonyLogsTable({ refreshState }) {
    const [filters, setFilters] = useState(() => {
        // Lets a shared link (e.g. "copy link" in the log details modal) land here with the
        // call_id filter already applied.
        const callId = new URLSearchParams(window.location.search).get("call_id");
        return callId ? { call_id: callId } : {};
    });
    const [companyResetKey, setCompanyResetKey] = useState(0);
    const toast = useToastDispatchContext();
    const navigate = useNavigate();

    const updateFilter = (name, value) =>
        setFilters((previous) => {
            const next = { ...previous };
            if (value === null || value === undefined || value === "") {
                delete next[name];
            } else {
                next[name] = value;
            }
            return next;
        });

    const cells = [
        { column: "Date", name: "occurred_at", sort: true, show: (v) => formatDateWithoutSeconds(v) },
        {
            column: "Call ID",
            name: "call_id",
            show: (value) =>
                value ? (
                    <span
                        style={{ color: "var(--main)", cursor: "pointer" }}
                        onClick={() =>
                            navigator.clipboard.writeText(value).then((_) => {
                                toast.success("Copy call ID to clipboard", "You can paste this call ID where you need !", 5000);
                            })
                        }
                    >
                        {getCutToken(value)}
                    </span>
                ) : (
                    "-"
                ),
        },
        { column: "From", name: "from_number" },
        { column: "To", name: "to_number" },
        { column: "Level", name: "level", show: (v) => <span className={`telephony-log-level telephony-log-level--${v}`}>{v}</span> },
        { column: "Event", name: "event_type", show: (v) => <span className="telephony-log-event">{v}</span> },
        { column: "Message", name: "message" },
        {
            column: "Company",
            name: ["company_logo", "company_name", "company_id"],
            show: ([logo, name, id]) => <CompanyCell logo={logo} name={name} id={id} onClick={() => switchCompany(id, navigate)} />,
        },
    ];

    const actions = [
        {
            className: "fas fa-eye action-button profil_action",
            style: {},
            title: "Details",
            onClick: ({ row }) => ModalManager.show(TelephonyLogDetailsModal, { data: row }),
        },
    ];

    const filterButton = (
        <div className="telephony-log-filters">
            <div className="telephony-log-level-filter">
                {LEVELS.map((lvl) => (
                    <span
                        key={lvl.value}
                        className={`telephony-log-level-toggle telephony-log-level-toggle--${lvl.className} ${(filters.level || "") === lvl.value ? "active" : ""}`}
                        onClick={() => updateFilter("level", lvl.value)}
                    >
                        {lvl.label}
                    </span>
                ))}
            </div>
            <InputSearch placeholderLibelle="Call ID" name="call_id" defaultValue={filters.call_id} onChange={(e) => updateFilter("call_id", e.target.value)} />
            <InputSearch placeholderLibelle="From" name="from_number" onChange={(e) => updateFilter("from_number", e.target.value)} />
            <InputSearch placeholderLibelle="To" name="to_number" onChange={(e) => updateFilter("to_number", e.target.value)} />
            <div className="telephony-log-company-filter">
                <AutoComplete
                    key={companyResetKey}
                    name="company_id"
                    className="telephony-log-company-autocomplete"
                    getItemValue={(name, item) => {
                        if (item && typeof item === "object" && typeof item.id === "number") {
                            updateFilter("company_id", item.id);
                        }
                    }}
                    apiCall={companyService.getCompaniesAutocomplete}
                    placeholder="Company"
                />
                {filters.company_id ? (
                    <i className="fas fa-times telephony-log-company-clear" title="Clear company filter"
                        onClick={() => { updateFilter("company_id", null);  setCompanyResetKey((key) => key + 1); }}
                    ></i>
                ) : null}
            </div>
        </div>
    );

    return (
        <Table name="telephony log" style={{ top: "0px" }} cells={cells}
            api={telephonyLogsService.getAllLogs}  actions={actions} buttons={[]}
            filters={[]}  filterValues={filters} filterButton={filterButton} refreshState={refreshState}
            silentRefresh={true}
        />
    );
}

export default TelephonyLogsTable;
